/**
 * Team, Matrix (plan docs/plans/lms-plan.md 8.4, 13 tab 6, Appendix A GET
 * /admin/matrix and /admin/matrix.csv; 18.16, 18.23; R16, R22, R23; WP-45).
 *
 * Replaces WP-03's placeholder in place: tabs/team/subviews.ts imports this
 * file's DEFAULT export (no props), mounted at /lms/team/matrix/* under the
 * Team tab's PageHeader and SegmentedTabs (plan 18.3).
 *
 * People (rows) who can reach the selected site against the courses
 * (columns) required of them by their profiles, plus any course they hold or
 * are assigned (8.4). Each cell is the status computeStatus gives it (8.4:
 * compliant green, expiring amber, assigned primary, overdue red, expired
 * red, waived gray, missing amber, not required gray, each with its word)
 * with the date that goes with it. Filters by site (the sites of this network
 * the admin can switch to; the server holds a named site to the admin's
 * reach, 18.23), group, status and course; the four tiles count every cell of
 * the filtered rows on every page (compliant, expiring in 30 days, overdue,
 * missing). "Export CSV" downloads the grid the filters select, every row
 * (paging is the screen's), as a text/csv attachment (18.23).
 *
 * A cell opens its details: the person page, and the waiver actions 8.4
 * allows from a cell. A cell carrying an OPEN assignment (assigned, overdue,
 * or compliant or expiring with an open renewal) can be waived with a reason;
 * a waived cell can have its waiver lifted (18.16: the cell's assignment_id is
 * the waived assignment there).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Download, FileQuestion, Grid3x3, Loader2 } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import Modal from '../../../../ui/Modal';
import { outlineBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { apiErrorMessage } from '../../../api/client';
import { saveBlob } from '../../../api/certificates';
import { listGroups } from '../../../api/groups';
import { downloadMatrixCsv, EXPIRING_TILE_DAYS, getMatrix, MATRIX_PAGE_LIMIT } from '../../../api/reports';
import type { MatrixCell, MatrixCourse, MatrixQuery, MatrixRow } from '../../../api/reports';
import { StatusPill } from '../../../components/StatusPill';
import {
  cardCls,
  formatDay,
  linkBtnCls,
  metaCls,
  microLabelCls,
  personName,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { COMPLIANCE_STATUSES, COMPLIANCE_STATUS_WORDS } from '../../../lib/complianceStatus';
import type { ComplianceStatus } from '../../../lib/complianceStatus';
import { LoadFailed, Loading, personHref, useAsync } from './shared';
import { UnwaiveForm, WaiveForm } from './WaiverForms';

/** Statuses whose cell carries the OPEN assignment (computeStatus, 18.16): these can be waived. */
const WAIVABLE: readonly ComplianceStatus[] = ['assigned', 'overdue', 'compliant', 'expiring'];

/** The date a cell shows under its word (the CSV's rule, 18.23). */
export function cellDateText(cell: MatrixCell): string | null {
  switch (cell.status) {
    case 'compliant':
      return cell.expires_at ? `until ${formatDay(cell.expires_at)}` : null;
    case 'expiring':
    case 'expired':
      return cell.expires_at ? formatDay(cell.expires_at) : null;
    case 'assigned':
    case 'overdue':
      return cell.due_at ? `due ${formatDay(cell.due_at)}` : null;
    default:
      return null;
  }
}

export function canWaiveCell(cell: MatrixCell): boolean {
  return Boolean(cell.assignment_id) && WAIVABLE.includes(cell.status);
}

export function canUnwaiveCell(cell: MatrixCell): boolean {
  return cell.status === 'waived' && Boolean(cell.assignment_id);
}

interface OpenCell {
  row: MatrixRow;
  course: MatrixCourse;
  cell: MatrixCell;
}

function Tile({ label, value, hint, icon: Icon, testId }: { label: string; value: number | null; hint?: string; icon: typeof Grid3x3; testId: string }) {
  return (
    <div className={cardCls} data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className={microLabelCls}>{label}</span>
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100" data-testid={`${testId}-value`}>
        {value === null ? '\u2014' : value.toLocaleString()}
      </div>
      {hint ? <div className={metaCls}>{hint}</div> : null}
    </div>
  );
}

export function MatrixView() {
  const { toast } = useToast();
  const { availableSites, currentSite } = useAuth();
  const [siteId, setSiteId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [status, setStatus] = useState('');
  const [courseId, setCourseId] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState<OpenCell | null>(null);
  const [mode, setMode] = useState<'details' | 'waive' | 'unwaive'>('details');
  // Course filter options: the columns of the latest answer that had no course filter
  // (a course-filtered answer carries that one column only).
  const [courseOptions, setCourseOptions] = useState<MatrixCourse[]>([]);

  const filters: MatrixQuery = useMemo(
    () => ({
      siteId: siteId || null,
      groupId: groupId || null,
      status: (status || null) as ComplianceStatus | null,
      courseId: courseId || null,
    }),
    [siteId, groupId, status, courseId]
  );
  const key = JSON.stringify({ ...filters, offset });

  const matrix = useAsync(
    () => getMatrix({ ...filters, limit: MATRIX_PAGE_LIMIT, offset }),
    key,
    'The compliance matrix could not be loaded.'
  );
  // useAsync drops a superseded answer, so a new data object always belongs to the current filters.
  useEffect(() => {
    const answer = matrix.data;
    if (!answer) return;
    if (!filters.courseId) setCourseOptions(answer.courses);
    else setCourseOptions((prev) => (prev.some((c) => c.id === filters.courseId) ? prev : [...prev, ...answer.courses]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix.data]);
  const groups = useAsync(() => listGroups({ limit: 200 }), 'groups', 'Groups could not be loaded for the filter.');

  const networkSites = useMemo(
    () =>
      (availableSites ?? [])
        .filter((s) => s.is_active !== false && (!currentSite?.network_id || s.network_id === currentSite.network_id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableSites, currentSite]
  );

  const change = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { blob, filename } = await downloadMatrixCsv(filters);
      saveBlob(blob, filename);
      toast('The matrix was exported as a CSV file.', { type: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'The matrix could not be exported. Try again.'), { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const closeCell = () => {
    setOpen(null);
    setMode('details');
  };
  const afterWaiverChange = () => {
    closeCell();
    matrix.reload();
  };

  const data = matrix.data;
  const rows = data?.rows ?? [];
  const courses = data?.courses ?? [];
  const page = data?.page;

  return (
    <div className="space-y-4" data-testid="lms-matrix">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Matrix filters">
        <FilterSelect label="Site" value={siteId} onChange={change(setSiteId)} auto>
          <option value="">{currentSite ? `This site (${currentSite.name})` : 'This site'}</option>
          {networkSites
            .filter((s) => s.id !== currentSite?.id)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </FilterSelect>
        <FilterSelect label="Group" value={groupId} onChange={change(setGroupId)} auto>
          <option value="">All people</option>
          {(groups.data?.items ?? [])
            .filter((g) => g.is_active)
            .map((g) => (
              <option key={g.id} value={g.id}>
                {g.kind === 'profile' ? `${g.name} (profile)` : g.name}
              </option>
            ))}
        </FilterSelect>
        <FilterSelect label="Status" value={status} onChange={change(setStatus)} auto>
          <option value="">All statuses</option>
          {COMPLIANCE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {COMPLIANCE_STATUS_WORDS[s]}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect label="Course" value={courseId} onChange={change(setCourseId)} auto>
          <option value="">All courses</option>
          {courseOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </FilterSelect>
        <div className="ml-auto flex items-center gap-2">
          {matrix.loading && data ? <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" /> : null}
          <button type="button" className={outlineBtnCls()} onClick={() => void exportCsv()} disabled={exporting} data-testid="matrix-export">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            Export CSV
          </button>
        </div>
      </div>
      {groups.error ? <p className={metaCls}>{groups.error}</p> : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="matrix-tiles">
        <Tile label="Compliant" value={data ? data.tiles.compliant : null} icon={CheckCircle2} testId="tile-compliant" hint="Cells with a current completion" />
        <Tile label="Expiring" value={data ? data.tiles.expiring : null} icon={Clock} testId="tile-expiring" hint={`Within ${EXPIRING_TILE_DAYS} days`} />
        <Tile label="Overdue" value={data ? data.tiles.overdue : null} icon={AlertTriangle} testId="tile-overdue" hint="Past the due date" />
        <Tile label="Missing" value={data ? data.tiles.missing : null} icon={FileQuestion} testId="tile-missing" hint="Required, nothing assigned" />
      </div>

      {matrix.error ? <LoadFailed message={matrix.error} onRetry={matrix.reload} testId="matrix-error" /> : null}
      {!data && matrix.loading ? <Loading rows={5} testId="matrix-loading" /> : null}

      {data && rows.length === 0 ? (
        <EmptyState
          icon={Grid3x3}
          variant="card"
          title="Nobody matches"
          hint={
            filters.groupId || filters.status || filters.courseId
              ? 'No one at this site matches these filters.'
              : 'Nobody who can reach this site has a required, assigned or completed course yet.'
          }
        />
      ) : null}

      {data && rows.length > 0 ? (
        <>
          <p className={metaCls} data-testid="matrix-site">
            {`People who can reach ${data.site.name ?? 'this site'}. Select a cell to see its details or to waive it.`}
          </p>
          <div className={tableCardCls}>
            <table className={tableCls} data-testid="matrix-table">
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={`${thCls} sticky left-0 z-10 bg-gray-50 dark:bg-gray-800`}>
                    Person
                  </th>
                  {courses.map((c) => (
                    <th key={c.id} scope="col" className={`${thCls} min-w-[8rem] whitespace-normal`} data-testid={`matrix-col-${c.id}`}>
                      {c.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {rows.map((row) => {
                  const name = personName(row.user);
                  return (
                    <tr key={row.user.id} data-testid={`matrix-row-${row.user.id}`}>
                      <th scope="row" className={`${tdCls} sticky left-0 z-10 bg-white text-left font-medium dark:bg-gray-800`}>
                        <Link to={personHref(row.user.id)} className={linkBtnCls} data-testid={`matrix-person-${row.user.id}`}>
                          {name}
                        </Link>
                        {row.user.email && row.user.full_name ? <div className={metaCls}>{row.user.email}</div> : null}
                      </th>
                      {courses.map((c) => {
                        const cell = row.cells[c.id] ?? { status: 'not_required' as ComplianceStatus };
                        const date = cellDateText(cell);
                        const word = COMPLIANCE_STATUS_WORDS[cell.status] ?? cell.status;
                        return (
                          <td key={c.id} className={tdCls}>
                            <button
                              type="button"
                              className="flex flex-col items-start gap-0.5 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                              onClick={() => {
                                setMode('details');
                                setOpen({ row, course: c, cell });
                              }}
                              aria-label={`${c.title} for ${name}: ${word}${date ? `, ${date}` : ''}`}
                              data-testid={`matrix-cell-${row.user.id}-${c.id}`}
                              data-status={cell.status}
                            >
                              <StatusPill status={cell.status} />
                              {date ? <span className={metaCls}>{date}</span> : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {page ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={metaCls} data-testid="matrix-page">
                {`Showing ${(page.offset + 1).toLocaleString()} to ${(page.offset + rows.length).toLocaleString()} of ${page.total.toLocaleString()} people`}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={secondaryBtnCls}
                  disabled={page.offset === 0 || matrix.loading}
                  onClick={() => setOffset(Math.max(0, page.offset - page.limit))}
                  data-testid="matrix-prev"
                >
                  Previous
                </button>
                <button
                  type="button"
                  className={secondaryBtnCls}
                  disabled={!page.hasMore || matrix.loading}
                  onClick={() => setOffset(page.offset + page.limit)}
                  data-testid="matrix-next"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <Modal isOpen={open !== null} onClose={closeCell} title={open ? open.course.title : 'Cell'} maxWidth="max-w-lg">
        {open ? (
          <CellDetails
            open={open}
            mode={mode}
            onMode={setMode}
            onCancel={() => setMode('details')}
            onDone={afterWaiverChange}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function CellDetails({
  open,
  mode,
  onMode,
  onCancel,
  onDone,
}: {
  open: OpenCell;
  mode: 'details' | 'waive' | 'unwaive';
  onMode: (m: 'details' | 'waive' | 'unwaive') => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const { row, course, cell } = open;
  const name = personName(row.user);
  if (mode === 'waive' && cell.assignment_id) {
    return <WaiveForm assignmentId={cell.assignment_id} personName={name} courseTitle={course.title} onCancel={onCancel} onDone={onDone} />;
  }
  if (mode === 'unwaive' && cell.assignment_id) {
    return <UnwaiveForm assignmentId={cell.assignment_id} personName={name} courseTitle={course.title} onCancel={onCancel} onDone={onDone} />;
  }
  return (
    <div className="space-y-3" data-testid="matrix-cell-details">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{name}</span>
        <StatusPill status={cell.status} />
      </div>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        {cell.due_at ? (
          <>
            <dt className={microLabelCls}>Due</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDay(cell.due_at)}</dd>
          </>
        ) : null}
        {cell.expires_at ? (
          <>
            <dt className={microLabelCls}>{cell.status === 'expired' ? 'Expired' : 'Expires'}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDay(cell.expires_at)}</dd>
          </>
        ) : null}
      </dl>
      {!canWaiveCell(cell) && !canUnwaiveCell(cell) ? (
        <p className={metaCls} data-testid="matrix-cell-no-action">
          {cell.status === 'missing'
            ? 'Nothing is assigned yet. The nightly profile check assigns it, or send it from Manage, Assign; it can be waived once it is assigned.'
            : 'There is no open assignment here to waive.'}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Link to={personHref(row.user.id)} className={secondaryBtnCls} data-testid="cell-person">
          Open person page
        </Link>
        {canUnwaiveCell(cell) ? (
          <button type="button" className={outlineBtnCls()} onClick={() => onMode('unwaive')} data-testid="cell-unwaive">
            Lift waiver
          </button>
        ) : null}
        {canWaiveCell(cell) ? (
          <button type="button" className={outlineBtnCls()} onClick={() => onMode('waive')} data-testid="cell-waive">
            Waive
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default MatrixView;
